import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ContactNav from './components/ContactNav';
import ContactFooter from './components/ContactFooter';

const BLUE_GREY = '#5b8ab5';
const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const contactSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Anna Schriver — Start a Video Project',
    description: 'Get in touch with Anna Schriver to start a filmmaking, video editing, or creative direction project. Available for freelance collaborations with brands, creators, and teams.',
    url: `${SITE_URL}/contact`,
    mainEntity: {
      '@type': 'Person',
      name: 'Anna Schriver',
      url: SITE_URL,
      jobTitle: 'Filmmaker, Video Editor & Creative Director',
      sameAs: ['https://www.instagram.com/anna_schriver/'],
      address: {
        '@type': 'PostalAddress',
        addressRegion: 'CO',
        addressCountry: 'US',
      },
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Freelance Filmmaking & Video Editing',
    description: 'Anna Schriver offers freelance filmmaking, video editing, and creative direction services for brands, athletes, and creators.',
    provider: {
      '@type': 'Person',
      name: 'Anna Schriver',
      url: SITE_URL,
    },
    areaServed: {
      '@type': 'State',
      name: 'Colorado',
    },
    serviceType: ['Filmmaking', 'Video Editing', 'Creative Direction'],
    url: `${SITE_URL}/contact`,
  },
];

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const id = 'schema-contact';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(contactSchema);
    document.title = 'Contact Anna Schriver — Start a Video Project';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Get in touch with Anna Schriver to start a filmmaking, video editing, or creative direction project. Available for freelance collaborations with brands, creators, and teams.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${SITE_URL}/contact`);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="project"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) {
      return;
    }
    setSubmitting(true);
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });
    try {
      await fetch('https://readdy.ai/api/form/d77eh3grnr7vi0ujqpe0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0d0d0f' }}>
      <ContactNav />

      {/* Cinematic bg glow */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(circle, rgba(91,138,181,0.06) 0%, transparent 70%)' }}></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(91,138,181,0.04) 0%, transparent 70%)' }}></div>
      </div>

      <div className="relative z-10 pt-12 pb-24 px-4 md:px-8 max-w-2xl mx-auto">
        {/* Header — no "businesses" text */}
        <div className="text-center mb-16">
          <h1 className="text-white leading-none mb-8" style={{ fontSize: 'clamp(4rem, 10vw, 7rem)' }}>
            <div style={{ fontWeight: 100 }}>LET&apos;S</div>
            <div style={{ fontWeight: 900 }}>WORK!</div>
          </h1>
          <p className="text-zinc-400 text-base leading-relaxed max-w-md mx-auto">
            I&apos;m always eager to partner with innovative businesses to bring their visions to life. If you&apos;re looking to create impactful video content that resonates with your audience, let&apos;s connect and make your project a success!
          </p>
        </div>

        {/* Form */}
        {submitted ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 flex items-center justify-center border mx-auto mb-6" style={{ borderColor: BLUE_GREY }}>
              <i className="ri-check-line text-3xl" style={{ color: BLUE_GREY }}></i>
            </div>
            <h2 className="text-white text-2xl font-black uppercase mb-4">Message Sent!</h2>
            <p className="text-zinc-400 mb-8">Thanks for reaching out. I&apos;ll get back to you soon.</p>
            <Link to="/" className="inline-block text-white px-8 py-3 text-sm font-bold tracking-widest uppercase cursor-pointer whitespace-nowrap rounded-md btn-glow" style={{ background: BLUE_GREY }}>
              BACK TO HOME
            </Link>
          </div>
        ) : (
          <form
            data-readdy-form
            id="contact-form"
            onSubmit={handleSubmit}
            className="space-y-8"
          >
            <div>
              <label className="block text-white text-xs font-bold tracking-widest uppercase mb-3">Company</label>
              <input
                type="text"
                name="company"
                placeholder="Company Name"
                className="w-full bg-transparent border border-white/20 text-white placeholder-zinc-600 px-4 py-4 text-sm focus:outline-none transition-colors"
                style={{ '--tw-ring-color': BLUE_GREY } as React.CSSProperties}
                onFocus={e => (e.currentTarget.style.borderColor = BLUE_GREY)}
                onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)')}
              />
            </div>

            <div>
              <label className="block text-white text-xs font-bold tracking-widest uppercase mb-3">Contact</label>
              <input
                type="text"
                name="contact"
                placeholder="Your Name"
                required
                className="w-full bg-transparent border border-white/20 text-white placeholder-zinc-600 px-4 py-4 text-sm focus:outline-none transition-colors"
                onFocus={e => (e.currentTarget.style.borderColor = BLUE_GREY)}
                onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)')}
              />
            </div>

            <div>
              <label className="block text-white text-xs font-bold tracking-widest uppercase mb-3">Email</label>
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                required
                className="w-full bg-transparent border border-white/20 text-white placeholder-zinc-600 px-4 py-4 text-sm focus:outline-none transition-colors"
                onFocus={e => (e.currentTarget.style.borderColor = BLUE_GREY)}
                onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)')}
              />
            </div>

            <div>
              <label className="block text-white text-xs font-bold tracking-widest uppercase mb-3">Project</label>
              <textarea
                name="project"
                placeholder="Your Project"
                rows={6}
                maxLength={500}
                className="w-full bg-transparent border border-white/20 text-white placeholder-zinc-600 px-4 py-4 text-sm focus:outline-none transition-colors resize-none"
                onFocus={e => (e.currentTarget.style.borderColor = BLUE_GREY)}
                onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)')}
              ></textarea>
              <p className="text-zinc-600 text-xs mt-1">Max 500 characters</p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full text-white py-4 text-sm font-bold tracking-widest uppercase cursor-pointer disabled:opacity-60 whitespace-nowrap rounded-md btn-glow"
              style={{ background: BLUE_GREY }}
            >
              {submitting ? 'SENDING...' : 'SEND MESSAGE'}
            </button>
          </form>
        )}
      </div>

      <ContactFooter />
    </div>
  );
}
